import { IconRefresh } from '@tabler/icons-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import {
  BasicBranchSelect,
  BasicDatePicker,
  BasicSelectionBox,
  ScalrDatePicker,
} from '@/components/customComponents'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

import {
  useInitializeDateContext,
  useReduxBaseFilter,
  useReduxComparisonFilter,
  useReduxSalesLensFilter,
  type BranchType,
} from '@/store'
import { DateType } from '@/utils/date'

interface FilterBarProps {
  pageId?: string
}

type FilterType = 'base' | 'comparison' | 'sales-lens-item'
type PageType = 'regular' | 'spend-pnl' | 'lens'

const getFilterType = (pageId: string): FilterType => {
  if (pageId === 'sales-lens-item') {
    return 'sales-lens-item'
  }
  if (pageId === 'pnl-lens' || pageId === 'labor-lens') {
    return 'comparison'
  }
  return 'base'
}

const getPageType = (pageId: string): PageType => {
  if (
    pageId === 'spend' ||
    pageId === 'profit-and-loss' ||
    pageId.includes('spend-lens') ||
    pageId.includes('pnl-lens')
  ) {
    return 'spend-pnl'
  }
  if (pageId.includes('sales-lens')) {
    return 'regular'
  }
  if (pageId.includes('lens')) {
    return 'lens'
  }
  return 'regular'
}

/**
 * REDUX-BASED FILTER BAR
 *
 * Uses pending state pattern - changes only apply when "Apply Filters" is clicked.
 *
 */
export default function FilterBar({ pageId }: FilterBarProps = {}) {
  const { t } = useTranslation()

  // ============================================================================
  // PAGE CONFIGURATION
  // ============================================================================
  const { id } = useParams()

  const effectivePageId = pageId || id || 'sales'

  const filterType = getFilterType(effectivePageId)
  const pageType = getPageType(effectivePageId)

  // Date context initialization
  // This handles context switching and date defaults automatically
  useInitializeDateContext(effectivePageId)

  // ============================================================================
  // REDUX FILTER HOOKS
  // ============================================================================

  // Select appropriate hook based on filter type
  // Pass pageId to all hooks for immediate correct context resolution
  const baseFilters = useReduxBaseFilter(effectivePageId)
  const comparisonFilters = useReduxComparisonFilter(effectivePageId)
  const salesLensFilters = useReduxSalesLensFilter(effectivePageId)

  const filters =
    filterType === 'sales-lens-item'
      ? salesLensFilters
      : filterType === 'comparison'
        ? comparisonFilters
        : baseFilters

  const {
    selectedBranch,
    selectedBranchType,
    setSelectedBranch,
    date,
    setDate,
    dateFilterType,
    setDateFilterType,
    resetFilters,
    refreshData,
  } = filters

  // Type-safe access to optional filter properties using type guards
  const comparisonBranch: string | undefined =
    'comparisonBranch' in filters
      ? (filters as any).comparisonBranch
      : undefined
  const setComparisonBranch: ((branch: string) => void) | undefined =
    'setComparisonBranch' in filters
      ? (filters as any).setComparisonBranch
      : undefined
  const temperatureFilter: string | undefined =
    'temperatureFilter' in filters
      ? (filters as any).temperatureFilter
      : undefined
  const sizeFilter: string | undefined =
    'sizeFilter' in filters ? (filters as any).sizeFilter : undefined
  const setTemperatureFilter: ((filter: string) => void) | undefined =
    'setTemperatureFilter' in filters
      ? (filters as any).setTemperatureFilter
      : undefined
  const setSizeFilter: ((filter: string) => void) | undefined =
    'setSizeFilter' in filters ? (filters as any).setSizeFilter : undefined

  // ============================================================================
  // PENDING STATE MANAGEMENT
  // ============================================================================

  // Pending states - what user has selected but not yet applied
  const [pendingBranch, setPendingBranch] = useState<string>(selectedBranch)
  const [pendingBranchType, setPendingBranchType] =
    useState<BranchType>(selectedBranchType)
  const [pendingDate, setPendingDate] = useState<DateRange | undefined>(date)
  const [pendingDateFilterType, setPendingDateFilterType] =
    useState<DateType>(dateFilterType)
  const [pendingComparisonBranch, setPendingComparisonBranch] = useState<
    string | undefined
  >(comparisonBranch)
  const [pendingTemperatureFilter, setPendingTemperatureFilter] = useState<
    string | undefined
  >(temperatureFilter)
  const [pendingSizeFilter, setPendingSizeFilter] = useState<
    string | undefined
  >(sizeFilter)

  // Reset refs for BasicSelectionBox components
  const temperatureFilterResetRef = useRef<boolean>(false)
  const sizeFilterResetRef = useRef<boolean>(false)

  // Track previous values to detect when reset actually happens
  const prevTemperatureFilterRef = useRef(temperatureFilter)
  const prevSizeFilterRef = useRef(sizeFilter)

  // Sync pending state with Redux state when Redux changes (on reset, page navigation, etc.)
  useEffect(() => {
    setPendingBranch(selectedBranch)
    setPendingBranchType(selectedBranchType)
    setPendingDate(date)
    setPendingDateFilterType(dateFilterType)
    setPendingComparisonBranch(comparisonBranch)
    setPendingTemperatureFilter(temperatureFilter)
    setPendingSizeFilter(sizeFilter)

    // Trigger reset for selection boxes when filters change from Redux (e.g., via reset button)
    // This ensures the visual selection updates when resetFilters() is called
    if (temperatureFilter !== prevTemperatureFilterRef.current) {
      temperatureFilterResetRef.current = true
      prevTemperatureFilterRef.current = temperatureFilter
    }
    if (sizeFilter !== prevSizeFilterRef.current) {
      sizeFilterResetRef.current = true
      prevSizeFilterRef.current = sizeFilter
    }
  }, [
    selectedBranch,
    selectedBranchType,
    date,
    dateFilterType,
    comparisonBranch,
    temperatureFilter,
    sizeFilter,
  ])

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  // Handle branch selection - updates pending state only
  const handleBranchChange = useCallback((selection: any) => {
    if (selection?.selectedBranchNames) {
      const newBranches = selection.selectedBranchNames.join(',')
      setPendingBranch(newBranches)
      // Update branch type from selection
      if (selection?.branchType) {
        setPendingBranchType(selection.branchType)
      }
    }
    if (selection?.selectedComparisonBranchNames) {
      const newComparisonBranches =
        selection.selectedComparisonBranchNames.join(',')
      setPendingComparisonBranch(newComparisonBranches)
    }
  }, [])

  // Handle date changes - updates pending state only
  const handleDateChange = useCallback(
    (newDate: DateRange | undefined, newDateFilterType: DateType) => {
      setPendingDate(newDate)
      setPendingDateFilterType(newDateFilterType)
    },
    []
  )

  // Handle refresh - triggers refetch in components
  const handleReload = useCallback(() => {
    refreshData(effectivePageId || 'unknown')
  }, [refreshData, effectivePageId])

  // Handle reset - restores defaults from Redux state
  const handleResetFilters = useCallback(() => {
    resetFilters()
  }, [resetFilters])

  // Apply all pending filter changes to Redux
  const applyFilters = useCallback(() => {
    setSelectedBranch(pendingBranch, pendingBranchType)
    setDate(pendingDate)
    setDateFilterType(pendingDateFilterType)

    if (setComparisonBranch && pendingComparisonBranch !== undefined) {
      setComparisonBranch(pendingComparisonBranch)
    }

    if (setTemperatureFilter && pendingTemperatureFilter) {
      setTemperatureFilter(pendingTemperatureFilter)
    }

    if (setSizeFilter && pendingSizeFilter) {
      setSizeFilter(pendingSizeFilter)
    }
  }, [
    pendingBranch,
    pendingBranchType,
    pendingDate,
    pendingDateFilterType,
    pendingComparisonBranch,
    pendingTemperatureFilter,
    pendingSizeFilter,
    setSelectedBranch,
    setDate,
    setDateFilterType,
    setComparisonBranch,
    setTemperatureFilter,
    setSizeFilter,
  ])

  // Check if there are pending changes to apply
  const hasPendingChanges = useCallback(() => {
    // Disable if no branch is selected
    if (!pendingBranch || pendingBranch.trim() === '') {
      return false
    }

    // Check core changes
    const coreChanges =
      pendingBranch !== selectedBranch ||
      pendingBranchType !== selectedBranchType ||
      pendingDateFilterType !== dateFilterType ||
      (!pendingDate && !!date) ||
      (!!pendingDate && !date) ||
      (pendingDate &&
        date &&
        (pendingDate.from?.getTime() !== date.from?.getTime() ||
          pendingDate.to?.getTime() !== date.to?.getTime()))

    // Check comparison branch changes
    const comparisonChanges =
      comparisonBranch !== undefined &&
      pendingComparisonBranch !== comparisonBranch

    // Check sales lens specific changes (ONLY for sales-lens-item page)
    const salesLensChanges =
      filterType === 'sales-lens-item' &&
      ((temperatureFilter !== undefined &&
        pendingTemperatureFilter !== temperatureFilter) ||
        (sizeFilter !== undefined && pendingSizeFilter !== sizeFilter))

    return coreChanges || comparisonChanges || salesLensChanges
  }, [
    filterType,
    pendingBranch,
    selectedBranch,
    pendingBranchType,
    selectedBranchType,
    pendingDate,
    date,
    pendingDateFilterType,
    dateFilterType,
    pendingComparisonBranch,
    comparisonBranch,
    pendingTemperatureFilter,
    temperatureFilter,
    pendingSizeFilter,
    sizeFilter,
  ])

  // ============================================================================
  // RENDER COMPONENTS
  // ============================================================================

  // Render primary branch selector
  const renderBranchSelector = () => (
    <BasicBranchSelect
      onChange={handleBranchChange}
      value={pendingBranch}
      branchType={pendingBranchType}
    />
  )

  // Render comparison branch selector for lens pages
  const renderComparisonBranchSelector = () => {
    if (!comparisonBranch || !setComparisonBranch) return null

    return (
      <>
        <BasicBranchSelect
          onChange={handleBranchChange}
          value={pendingComparisonBranch || ''}
        />
      </>
    )
  }

  // Render date picker based on page type
  const renderDatePicker = () => {
    // Regular pages and sales-lens use ScalrDatePicker
    if (pageType === 'regular') {
      return (
        <ScalrDatePicker
          date={pendingDate}
          dateFilterType={pendingDateFilterType}
          onChange={handleDateChange}
        />
      )
    }

    // Spend/PnL and lens pages use BasicDatePicker
    if (pageType === 'spend-pnl' || pageType === 'lens') {
      return (
        <BasicDatePicker
          value={pendingDate}
          dateFilterType={pendingDateFilterType}
          onChange={handleDateChange}
          type={effectivePageId.includes('spend') ? 'spend' : 'pnl'}
        />
      )
    }

    return null
  }

  // Render sales lens specific filters (ONLY for sales-lens-item page)
  const renderSalesLensFilters = () => {
    // Only show temperature and size filters on sales-lens-item page
    if (filterType !== 'sales-lens-item') return null
    if (!temperatureFilter || !setTemperatureFilter) return null

    return (
      <>
        <BasicSelectionBox
          options={[
            { value: 'all', label: t('filters.temperature.all') },
            { value: 'hot', label: t('filters.temperature.hot') },
            { value: 'iced', label: t('filters.temperature.iced') },
            { value: 'cold', label: t('filters.temperature.cold') },
            { value: 'frozen', label: t('filters.temperature.frozen') },
          ]}
          onValueChange={setPendingTemperatureFilter}
          defaultValue={pendingTemperatureFilter || 'all'}
          resetSelection={temperatureFilterResetRef}
        />
        {setSizeFilter && (
          <BasicSelectionBox
            options={[
              { value: 'all', label: t('filters.size.all') },
              { value: 'small', label: t('filters.size.small') },
              { value: 'medium', label: t('filters.size.medium') },
              { value: 'large', label: t('filters.size.large') },
            ]}
            onValueChange={setPendingSizeFilter}
            defaultValue={pendingSizeFilter || 'all'}
            resetSelection={sizeFilterResetRef}
          />
        )}
      </>
    )
  }

  // Render action buttons
  const renderActionButtons = () => (
    <div className="flex gap-4 justify-center md:justify-start items-center">
      <Button
        variant="outline"
        onClick={handleReload}
        className="h-10 w-10 p-0 bg-white"
        title={t('filters.buttons.refreshData')}
      >
        <IconRefresh className="h-5 w-5" />
      </Button>
      <Button variant="outline" onClick={handleResetFilters} size="sm">
        {t('filters.buttons.resetFilters')}
      </Button>
      <Button onClick={applyFilters} size="sm" disabled={!hasPendingChanges()}>
        {t('filters.buttons.applyFilters')}
      </Button>
    </div>
  )

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <Card className="sticky top-0 z-40">
      <CardContent className="p-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          {/* Filter Controls */}
          <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
            {renderBranchSelector()}
            {renderComparisonBranchSelector()}
            {renderDatePicker()}
            {renderSalesLensFilters()}
          </div>

          {/* Action Buttons */}
          {renderActionButtons()}
        </div>
      </CardContent>
    </Card>
  )
}
